import { useMutation, useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo, useCallback } from 'react';

import Comments from 'components/Comments';

import useQueryClient from 'hooks/useQueryClient';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { commentQueryKeys, reviewQueryKeys } from 'lib/queryKeys';
import { addDislikeToReview, addLikeToReview } from 'lib/reviews';

import DomainProvider from 'providers/DomainProvider';
import { useUser } from 'providers/UserProvider';

import { getOfferCommentsList } from 'services/comment';
import { getUserSubscription, postUserOfferSubscription } from 'services/offer';

import COMMENT from 'constants/comment';
import OFFER from 'constants/offer';

const EmptyCommentsList = dynamic(() => import('./EmptyCommentsList'));

const QUICK_COMMENTS = [
  {
    emojiLeft: '🚀',
    gaEventCategory: 'message_text_greatprice',
    text: 'Excelente Preço',
  },
  {
    emojiLeft: '🧐',
    gaEventCategory: 'message_text_specialists',
    text: 'Entendedores?',
  },
  {
    emojiLeft: '😢',
    gaEventCategory: 'message_text_icant',
    text: 'Não Consegui',
  },
  {
    emojiLeft: '🤩',
    gaEventCategory: 'message_text_ican',
    text: 'Consegui!',
  },
];

const OfferCommentsList = ({
  aliasId = null,
  className,
  gaEventCategory,
  initialComments,
  isReadOnly,
  offerCommentsCount,
  offerId,
  offerStatusName,
}) => {
  const queryClient = useQueryClient();
  const { isLoggedIn, loadingLoggedInUser, runCallbackIfLoggedIn } = useUser();
  const isOfferAllowedToReceiveNewComments =
    !isReadOnly && offerStatusName !== OFFER.STATUSES.WAITING;

  const { data: isSubscribed } = useQuery({
    queryKey: commentQueryKeys.offer.isSubscribedForNotifications({
      offerId,
    }),
    queryFn: ({ signal }) => getUserSubscription({ offerId }, { signal }),
    select: (data) => !!data?.isSubscribed,
    enabled:
      !loadingLoggedInUser && isLoggedIn && isOfferAllowedToReceiveNewComments,
    cacheTime: 0,
    staleTime: Infinity,
    refetchOnMount: false,
    useErrorBoundary: true,
  });

  const { mutate: mutateSubscription } = useMutation({
    mutationFn: postUserOfferSubscription,
    useErrorBoundary: true,
    onMutate: () => {
      if (!isLoggedIn) {
        return;
      }

      sendEvent({
        action: `notification_comments_${
          isSubscribed ? 'disabled' : 'enabled'
        }`,
        category: gaEventCategory,
      });
      queryClient.setQueryData(
        commentQueryKeys.offer.isSubscribedForNotifications({ offerId }),
        { isSubscribed: !isSubscribed }
      );
    },
  });

  const onSubscribe = useCallback(() => {
    if (!isLoggedIn) {
      sendEvent({
        action: 'notification_comments_enabled_unlogged',
        category: gaEventCategory,
      });
    }

    runCallbackIfLoggedIn(mutateSubscription, { offerId });
  }, [offerStatusName, isLoggedIn]);

  const onLike = (modifiedCommentId, action) => {
    queryClient.setInfiniteQueryData(
      reviewQueryKeys.list({ aliasId, sort: COMMENT.REVIEWS_SORT }),
      (pages) =>
        pages.map((page) => ({
          ...page,
          data: page.data.map((review) =>
            addLikeToReview(modifiedCommentId, review, action)
          ),
        }))
    );
  };

  const onDislike = (modifiedCommentId, action) => {
    queryClient.setInfiniteQueryData(
      reviewQueryKeys.list({ aliasId, sort: COMMENT.REVIEWS_SORT }),
      (pages) =>
        pages.map((page) => ({
          ...page,
          data: page.data.map((review) =>
            addDislikeToReview(modifiedCommentId, review, action)
          ),
        }))
    );
  };

  return (
    <DomainProvider domain={OFFER.DOMAIN} domainId={offerId}>
      <Comments.Root
        gaEventCategory={gaEventCategory}
        initialSort={COMMENT.SORT_VALUES.RELEVANCE}
        onDislike={onDislike}
        onLike={onLike}
      >
        <Comments.Section className={className}>
          <Comments.Header
            emptyListComponent={EmptyCommentsList}
            isSortActive={isOfferAllowedToReceiveNewComments}
            isSubscribed={isSubscribed}
            isSubscriptionAllowed
            onSubscribe={onSubscribe}
          />
          <Comments.List
            aliasId={aliasId}
            commentsCount={offerCommentsCount}
            initialComments={initialComments}
            queryFn={getOfferCommentsList}
            canCommentsBeRecommendedAsReview
          />
          <div className="min-h-[250px]">
            {isOfferAllowedToReceiveNewComments ? (
              <Comments.TextEditor gaEventCategory={gaEventCategory}>
                <Comments.QuickComments
                  className="min-h-[40px]"
                  messages={QUICK_COMMENTS}
                />
              </Comments.TextEditor>
            ) : (
              <Comments.ReadOnlyAlert
                message={
                  offerStatusName === OFFER.STATUSES.WAITING
                    ? placeholder(
                        'descriptions.offerWaitingToBeApprovedToAllowReceiveComments'
                      )
                    : placeholder(
                        'descriptions.offerDisallowedToReceiveComments'
                      )
                }
              />
            )}
          </div>
        </Comments.Section>
      </Comments.Root>
    </DomainProvider>
  );
};

OfferCommentsList.propTypes = {
  aliasId: PropTypes.number,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  initialComments: PropTypes.arrayOf(
    PropTypes.shape({
      commentId: PropTypes.number.isRequired,
      parentId: PropTypes.number,
      userId: PropTypes.number.isRequired,
      commentText: PropTypes.string.isRequired,
      commentRaw: PropTypes.string.isRequired,
      commentDate: PropTypes.string.isRequired,
      commentLikes: PropTypes.number.isRequired,
      commentStatusName: PropTypes.string.isRequired,
      commentFixed: PropTypes.bool.isRequired,
      relevance: PropTypes.number.isRequired,
      commentStars: PropTypes.number,
      reviewAliasId: PropTypes.number,
      edited: PropTypes.bool.isRequired,
      isReview: PropTypes.bool.isRequired,
      userLevel: PropTypes.number.isRequired,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userSpecialtyName: PropTypes.string,
      userTypeName: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
      offerPhoto: PropTypes.string,
      replies: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    })
  ).isRequired,
  isReadOnly: PropTypes.bool,
  offerCommentsCount: PropTypes.number.isRequired,
  offerId: PropTypes.number.isRequired,
  offerStatusName: PropTypes.string.isRequired,
};

export default memo(OfferCommentsList);
